import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Banner,
  Cell,
  List,
  Placeholder,
  Section,
  Spinner,
  Title,
} from "@telegram-apps/telegram-ui";

import { useAppData } from "../AppData";
import { useBackButton } from "../hooks/useBackButton";
import { useJobPolling } from "../hooks/useJobPolling";
import { useMainButton } from "../hooks/useMainButton";
import type { OperationCategory } from "../types";

const CATEGORY_PATH: Record<OperationCategory, string> = {
  calc: "/calc",
  views: "/views",
  report: "/report",
};

export function JobStatusPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { job, error } = useJobPolling(jobId ?? null);
  const { operations } = useAppData();

  const op = job ? operations.find((o) => o.id === job.operation_id) : undefined;
  const backPath = op ? CATEGORY_PATH[op.category] : "/calc";
  const back = useCallback(() => navigate(backPath), [navigate, backPath]);
  useBackButton(true, back);

  const finished = job?.status === "done" || job?.status === "failed";
  useMainButton({
    text: "В меню",
    visible: !!finished,
    enabled: true,
    onClick: back,
  });

  if (error && !job) {
    return <Placeholder header="Ошибка" description={error} />;
  }
  if (!job) {
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <Spinner size="m" />
      </div>
    );
  }

  const pct =
    job.total > 0 ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;

  const statusText: Record<typeof job.status, string> = {
    pending: "Подготовка...",
    running: "Обработка...",
    done: "Готово",
    failed: "Ошибка",
  };

  return (
    <div className="page">
      <List>
        <Section header="Статус задачи">
          <div style={{ padding: "12px 16px" }}>
            <Title weight="2" level="3">
              {statusText[job.status]}
            </Title>
            <div style={{ marginTop: 12 }}>
              <div className="progress-bar">
                <div style={{ width: `${pct}%` }} />
              </div>
              <div style={{ marginTop: 6 }} className="muted">
                {job.processed} из {job.total} ({pct}%)
              </div>
            </div>
          </div>
        </Section>

        {job.status === "failed" && job.fatal_error && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Фатальная ошибка" description={job.fatal_error} />
          </div>
        )}

        {job.errors.length > 0 && (
          <Section header={`Ошибки в строках (${job.errors.length})`}>
            {job.errors.slice(0, 50).map((err, i) => (
              <Cell
                key={`${err.row_match}-${i}`}
                subtitle={err.message}
                multiline
              >
                {err.row_match || "(пусто)"}
              </Cell>
            ))}
          </Section>
        )}

        {job.status === "done" && job.errors.length === 0 && (
          <div style={{ padding: "8px 16px" }}>
            <Banner type="section" header="Подсчёт таблицы закончен" />
          </div>
        )}
      </List>
    </div>
  );
}
